import { images } from "../../../../Assets/assetsHelper";
import "./JudgingReport.css";
import { BarLoader } from "react-spinners";
import { useNavigate } from "react-router";
import { display, fontSize, maxWidth } from "@mui/system";
import { Box, Rating, Typography, Button, Stack } from "@mui/material";
import Paper from "@mui/material/Paper";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell, { tableCellClasses } from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TablePagination from "@mui/material/TablePagination";
import TableRow from "@mui/material/TableRow";
import { styled } from "@mui/material/styles";
import FavoriteIcon from "@mui/icons-material/Favorite";
import FavoriteBorderIcon from "@mui/icons-material/FavoriteBorder";
import TripOriginIcon from "@mui/icons-material/TripOrigin";
import CircleIcon from "@mui/icons-material/Circle";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import { useEffect, useState } from "react";
import PrimaryButton from "../../../../Components/Atoms/Buttons/Primary/PrimaryButton";
import TextArea from "../../../../Components/Atoms/Inputs/TextArea/TextArea";
import { toast } from "react-toastify";
import useSave from "../../../../Hooks/useSave";
import useDelete from "../../../../Hooks/useDelete";
import { missionJudgingURL, missionsURL } from "../../../../Hooks/URL";

// Utility function to calculate optimal number of rating buttons
const calculateOptimalRatingButtons = (maxValue) => {
  // Always return 5 rating buttons
  return maxValue >= 10 ? 10 : 5;
};

const AttributeRating = ({ attribute, i, setScores, scores }) => {
  const [value, setValue] = useState(null);
  const [hover, setHover] = useState(-1);

  const maxValue = attribute?.maxValue || 10;
  const optimalRatingButtons = calculateOptimalRatingButtons(maxValue);
  const divisor = maxValue / optimalRatingButtons; // Always divide maxValue by 5 for each point

  return (
    <>
      <TableContainer component={Paper} sx={{ marginBottom: { xs: "5px" } }}>
        <Table aria-label="simple table">
          <TableBody>
            <TableRow>
              <TableCell
                align="left"
                sx={{
                  padding: { xs: "10px 10px 4px" },
                  fontSize: { xs: "15px", md: "18px" },
                  display: { xs: "block", md: "flex" },
                  flexDirection: { xs: "column", md: "row" },
                  justifyContent: "space-between",
                }}
              >
                {i + 1} - {attribute?.name}
                <span className="scoreSmall">
                  {attribute?.value}/{attribute?.maxValue}
                </span>
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell>
                <Rating
                  className="rating"
                  readOnly
                  name="hover-feedback"
                  value={
                    maxValue >= 10
                      ? Math.round(attribute?.value / divisor)
                      : attribute?.value
                  }
                  icon={<CircleIcon color="#6a0505" fontSize="inherit" />}
                  emptyIcon={<TripOriginIcon fontSize="inherit" />}
                  max={optimalRatingButtons}
                />
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>
    </>
  );
};

function JudgingReport({ loading, url, report, missionJudgingId }) {
  const navigate = useNavigate();

  const [go, setGo] = useState(false);
  const { state } = useDelete(
    `${missionJudgingURL}/${missionJudgingId}/teams/${report?.team?.id}/scoringReport`,
    go
  );

  useEffect(() => {
    if (state?.dataRes?.status == "success") {
      navigate(-1);
    }
  }, [state]);

  const handleEdit = () => {
    navigate(`/missions/${missionJudgingId}/teams/${report?.team?.id}/edit`);
  };

  return (
    <div className="mainContainer">
      {report && !loading ? (
        <div className="Cont">
          {!report?.mission?.missionJudgingIsCompleted ? (
            <>
              {/* Action Buttons */}
              <Box
                sx={{
                  width: "100%",
                  display: "flex",
                  justifyContent: "flex-end",
                  margin: "20px 0",
                  gap: 2,
                }}
              >
                {!report?.absent ? (
                  <Button
                    variant="contained"
                    startIcon={<EditIcon />}
                    onClick={handleEdit}
                    sx={{
                      backgroundColor: "#1976d2",
                      color: "white",
                      "&:hover": {
                        backgroundColor: "#1565c0",
                      },
                      textTransform: "none",
                      fontWeight: 600,
                    }}
                  >
                    Edit Report
                  </Button>
                ) : (
                  ""
                )}
                <Button
                  variant="outlined"
                  startIcon={<DeleteIcon />}
                  onClick={() => {
                    const confirmed = window.confirm(
                      "Are you sure you want to delete this report? This action cannot be undone."
                    );
                    if (confirmed) {
                      setGo(true);
                    }
                  }}
                  sx={{
                    borderColor: "#d32f2f",
                    color: "#d32f2f",
                    "&:hover": {
                      borderColor: "#c62828",
                      backgroundColor: "rgba(211, 47, 47, 0.04)",
                    },
                    textTransform: "none",
                    fontWeight: 600,
                  }}
                >
                  Delete Report
                </Button>
              </Box>
            </>
          ) : (
            ""
          )}

          <div className="greyBox">
            <Box sx={{ width: "100%", display: "flex", flexWrap: "wrap" }}>
              <div className="greyNumberBox" style={{ minWidth: "200px" }}>
                <div>
                  <h4>Mission Name: </h4>
                  <h3>{report?.mission?.name}</h3>
                </div>
              </div>
              <div className="greyNumberBox" style={{ minWidth: "200px" }}>
                <div>
                  <h4>Mission Number: </h4>
                  <h3>{report?.mission?.order}</h3>
                </div>
              </div>
              <div className="greyNumberBox" style={{ minWidth: "200px" }}>
                <div>
                  <h4>Category: </h4>
                  <h3>{report?.team?.category?.name}</h3>
                </div>
              </div>
              <div className="greyNumberBox" style={{ minWidth: "200px" }}>
                <div>
                  <h4>Age Group: </h4>
                  <h3>{report?.team?.category?.ageGroup?.name}</h3>
                </div>
              </div>
              <div className="greyNumberBox" style={{ minWidth: "200px" }}>
                <div>
                  <h4>Team Name: </h4>
                  <h3>{report?.team?.name}</h3>
                </div>
              </div>
              <div className="greyNumberBox" style={{ minWidth: "200px" }}>
                <div>
                  <h4>Team Code: </h4>
                  <h3>{report?.team?.code}</h3>
                </div>
              </div>
              <div className="greyNumberBox" style={{ minWidth: "200px" }}>
                <div>
                  <h4>Date: </h4>
                  <h3>{report?.dateTime?.split("T")?.at(0)}</h3>
                </div>
              </div>
              <div className="greyNumberBox" style={{ minWidth: "200px" }}>
                <div>
                  <h4>Time: </h4>
                  <h3>{report?.dateTime?.split("T")?.at(1)}</h3>
                </div>
              </div>
              {report?.absent ? (
                <div className="greyNumberBox" style={{ minWidth: "200px" }}>
                  <div>
                    <h4>Absent: </h4>
                    <h3>{report?.absent ? "Yes" : "No"}</h3>
                  </div>
                </div>
              ) : (
                ""
              )}
            </Box>
          </div>
          <Box
            sx={{
              width: "100%",
              // display: "flex",
              // justifyContent: "space-between",
              alignItems: "center",
              margin: "auto",
              marginBottom: "0px",
              maxWidth: "1000px",
            }}
          >
            {!report?.absent ? (
              <>
                <Box
                  sx={{
                    width: "100%",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    margin: "auto",
                    marginBottom: "0px",
                  }}
                >
                  <Typography
                    variant={"h3"}
                    sx={{
                      fontSize: { xs: "20px", md: "25px" },
                      margin: "10px 0",
                      textAlign: "start",
                      fontWeight: "700",
                    }}
                  >
                    Scoring Sheet
                  </Typography>
                  <Typography
                    variant={"h3"}
                    sx={{
                      fontSize: { xs: "20px", md: "25px" },
                      margin: "10px 0",
                      textAlign: "start",
                      fontWeight: "700",
                    }}
                  >
                    Total: {report?.totalValue} / {report?.maxTotalValue}
                  </Typography>
                </Box>

                <div
                  className="greyBox"
                  style={{ flexDirection: "column", alignItems: "flex-start" }}
                >
                  {report?.groups?.map((group, i) => (
                    <>
                      <Typography
                        variant={"h3"}
                        sx={{
                          fontSize: { xs: "17px", md: "22px" },
                          margin: "10px 0",
                          textAlign: "start",
                          fontWeight: "700",
                        }}
                      >
                        {i + 1} - {group?.name}
                      </Typography>
                      {group?.attributes?.map((attribute, i) => (
                        <AttributeRating
                          attribute={attribute}
                          i={i}
                          scores={attribute?.report}
                        />
                      ))}
                    </>
                  ))}
                  <Typography
                    variant={"h3"}
                    sx={{
                      fontSize: { xs: "17px", md: "22px" },
                      margin: "10px 0",
                      textAlign: "start",
                      fontWeight: "700",
                    }}
                  >
                    {report?.groups?.length + 1} - Write Your Note:
                  </Typography>
                  <div style={{ margin: "0", width: "100%" }}>
                    <TextArea
                      readOnly
                      name="text"
                      placeholder={"Enter your note about this team.."}
                      classes="textAreaInputFull"
                      label="Note"
                      newValue={report?.note}
                    />
                  </div>
                </div>
              </>
            ) : (
              ""
            )}
          </Box>
        </div>
      ) : (
        <div className="loadingBox">
          <BarLoader color="#6a0505" />
        </div>
      )}
    </div>
  );
}

export default JudgingReport;
